import { Badge, BadgeProps, Button, ButtonSizes, ButtonStyles, Icon, Icons } from '@silverfort/sf-components';
import { ReactNode } from 'react';
import useHideTowerLayout from '../../../TowerLayout/hooks/hideTowerLayout';

export type BlockPageProps = {
    leftContent: LeftContentProps;
    rightContent: RightContentProps;
};

type LeftContentProps = {
    titleIntroText?: string;
    title: string;
    subTitle?: string;
    image: string;
    backgroundColor?: string;
};

type RightContentProps = {
    badgeProps?: BadgeProps;
    icon: (typeof Icons)[keyof typeof Icons];
    title: string;
    description: ReactNode;
    topics: ReactNode[];
    accessLink: string;
};

export const BlockPage = ({ leftContent, rightContent }: BlockPageProps) => {
    useHideTowerLayout();

    return (
        <div className="h-screen ml-[64px] w-[calc(100%-64px)] z-[2000] flex">
            <LeftContent {...leftContent} />
            <RightContent {...rightContent} />
        </div>
    );
};

const LeftContent = ({
    titleIntroText = 'Introducing',
    title,
    subTitle,
    image,
    backgroundColor = '#F5FAFF'
}: LeftContentProps) => {
    return (
        <div
            className={`w-[50%] max-[1920px]:ps-16 max-[1920px]:pt-20 min-[1920px]:ps-[228px] min-[1920px]:pt-[78px] flex flex-col gap-12 overflow-hidden bg-[${backgroundColor}]`}>
            <div className="flex flex-col gap-2">
                <span className="font-sf text-14 text-sf-gray-500 h-[17px]">{titleIntroText}</span>
                <div className="flex items-center">
                    <span className="font-sf-poppins text-[21px] font-medium text-sf-gray-800 leading-[23px] tracking-[0.529px]">
                        {title}{' '}
                        {subTitle && (
                            <span className="text-13 font-sf-poppins text-sf-gray-500 leading-[15px] tracking-[0.203px]">
                                {subTitle}
                            </span>
                        )}
                    </span>
                </div>
            </div>
            <div className="bg-sf-primary-100 max-[1920px]:rounded-[16.32px] max-[1920px]:p-[16.32px] min-[1920px]:rounded-[19.59px] min-[1920px]:p-[19.59px] max-[1920px]:shadow-[0px_7.61px_15.23px_0px_#0000001A] min-[1920px]:shadow-[0px_9.14px_18.29px_0px_#0000001A] max-[1920px]:w-[799.55px] max-[1920px]:h-120 min-[1920px]:w-[960px] min-[1920px]:h-[576.33px] relative overflow-hidden">
                <div className="max-[1920px]:rounded-[10.875px] min-[1920px]:rounded-[13.057px] w-full h-full overflow-hidden">
                    <img className="w-full h-auto" src={image} alt="mainImage" />
                </div>
            </div>
        </div>
    );
};

const RightContent = ({ badgeProps, icon, title, description, topics, accessLink }: RightContentProps) => {
    const hasBadge = !!badgeProps;

    const handleGetAccessClick = () => {
        window.open(accessLink, '_blank');
    };

    return (
        <div className="relative w-[50%] p-16 bg-sf-white">
            <div className="w-full max-w-[647px] max-[1920px]:h-[592px] min-[1920px]:h-[684.33px] flex flex-col justify-between">
                <div className="flex flex-col gap-4">
                    {hasBadge && <Badge {...badgeProps} className={`w-fit self-end ${badgeProps?.className || ''}`} />}
                    <div className="flex flex-col gap-8">
                        <div className="flex flex-col gap-2">
                            <Icon size={32} icon={icon} className="text-sf-gray-400" />
                            <span className="font-sf-poppins text-[18px] font-medium text-sf-gray-800 leading-[20px] tracking-[0.389px]">
                                {title}
                            </span>
                            <span className="font-sf text-13 text-sf-gray-500 leading-[19px] tracking-[0.203px]">
                                {description}
                            </span>
                        </div>
                        <div className="flex flex-col gap-4">
                            {topics.map((content, index) => (
                                <div key={index} className="flex gap-2">
                                    <Icon icon={Icons.Check} size={20} className="min-w-5 min-h-5 text-sf-gray-400" />
                                    <span
                                        key={index}
                                        className="font-sf text-sf-gray-800 text-14 leading-[21px] tracking-[0.235px]">
                                        {content}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <Button
                    text="Get Access"
                    className="self-end"
                    buttonStyle={ButtonStyles.PrimarySolid}
                    buttonSize={ButtonSizes.Large}
                    onClick={handleGetAccessClick}
                />
            </div>
        </div>
    );
};
